import { type FC, useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { TextField } from "@mui/material";
import { IBookingSalesAndPaymentsResult, PaymentTypes } from "../../../../../logic/api";
import DropDownField, { IDropDownListItem } from "../../../../../components/editable/EditableField/fields/DropDownField";
import flex from "../../../../../styles/flex";
import { SessionContext } from "../../../../../utility/useSession";
import { usePreferences } from "../../../../../utility/usePreferences";
import { formatCurrency, paymentTypeLabelMap } from "../../../../../utility/utils";
import { RootState, useAppDispatch } from "../../../../../state/store";
import {
  updatePaymentAccountNumber,
  updatePaymentAuthCode,
  updatePaymentDescription,
  updatePaymentType,
} from "../../../../../state/reducers/paymentsReducer";
import { useTranslation } from "react-i18next";
import { useReleaseManagement, ReleaseFlag } from "@/utility/feature-management";

interface IPaymentDialogContentProps {
  applyPayment: () => void;
  onClose: (updated: boolean) => void;
  outstanding: number;
  partialPayment: boolean;
  saleData: IBookingSalesAndPaymentsResult;
}

const PaymentDialogContent: FC<IPaymentDialogContentProps> = ({
  outstanding,
  saleData,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [helperText, setHelperText] = useState("");

  const accountNumber = useSelector((state: RootState) => state.payments.accountNumber);
  const authCode = useSelector((state: RootState) => state.payments.authCode);
  const description = useSelector((state: RootState) => state.payments.description);
  const paymentType = useSelector((state: RootState) => state.payments.paymentType);

  const isMakePaymentDescriptionMandatoryForComp = useReleaseManagement(
    ReleaseFlag.releaseMakePaymentDescriptionMandatoryForComp
  );
  const isFloatingPointPaymentFixEnabled = useReleaseManagement(
    ReleaseFlag.releaseFloatingPointPaymentFix
  );

  const session = useContext(SessionContext);
  const {
    usesPaymentMethod,
    isEligibleRefundPaymentMethod,
    isEligibleCardPaymentMethod,
    isEligibleAccountPaymentMethod,
  } = usePreferences();

  const isCompDiscount = paymentType === PaymentTypes.compDiscount;
  const isRefund = paymentType === PaymentTypes.refund;

  const shouldValidateDescription =
    isMakePaymentDescriptionMandatoryForComp && (isCompDiscount || isRefund);

  const descriptionRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (shouldValidateDescription && descriptionRef.current) {
      descriptionRef.current.focus();
    }

    if (shouldValidateDescription && !description?.trim()) {
      setHelperText("errors.requiredDescription");
    } else {
      setHelperText("");
    }

  }, [shouldValidateDescription, description]);

  return (
    <div style={{ ...flex.flexRows, width: 520, marginTop: 20 }}>
      <div style={flex.flexRows}>
        <div style={flex.flexColumns}>
          <div style={{ width: 220, marginRight: 20 }}>
            <DropDownField
              label={t("input.labels.method")}
              testId="payment-method-select"
              selectedItem={{ name: "", value: paymentType?.toString() || "" }}
              onChange={(item) => {
                const paymentType = Number(item?.value);
                dispatch(updatePaymentType({ paymentType, floatingPointFixEnabled: isFloatingPointPaymentFixEnabled }));
              }}
              onRequestData={() => {
                const items = Object.values(PaymentTypes)
                  .filter((pt) => !(
                    typeof pt === "string" ||
                    !usesPaymentMethod(pt) ||
                    (!outstanding && !isEligibleRefundPaymentMethod(pt))
                  ))
                  .map((pt): IDropDownListItem => {
                    return {
                      name: paymentTypeLabelMap.get(pt as PaymentTypes) || t("general.unknown"),
                      value: pt.toString(),
                    };
                  })
                  .filter(Boolean);

                return Promise.resolve([
                  { name: t("payments.selectMethod"), value: "" },
                  ...items,
                ]);
              }}
            />
          </div>

          <div style={{ ...flex.flexFill, marginTop: 20 }}>
            {isEligibleRefundPaymentMethod(paymentType) && (
              <div style={{ fontSize: 16 }}>
                <b>
                  {t("payments.maximumRefund")}:{" "}
                  {formatCurrency(saleData.total - saleData.outstanding, session)}
                </b>
              </div>
            )}

            {isEligibleCardPaymentMethod(paymentType) && (
              <TextField
                error={!authCode}
                label={t("input.labels.authCode")}
                variant="outlined"
                size="small"
                inputProps={{ width: "220px", "data-testid": "payment-authcode-input" }}
                value={authCode || ""}
                onChange={(e) => dispatch(updatePaymentAuthCode(e.target.value))}
              />
            )}

            {isEligibleAccountPaymentMethod(paymentType) && (
              <TextField
                error={!accountNumber}
                label={t("input.labels.accountNumber")}
                variant="outlined"
                size="small"
                inputProps={{ width: "220px", "data-testid": "payment-accountnumber-input" }}
                value={accountNumber || ""}
                onChange={(e) => dispatch(updatePaymentAccountNumber(e.target.value))}
              />
            )}
          </div>
        </div>

        <TextField
          inputRef={descriptionRef}
          error={shouldValidateDescription && !description?.trim()}
          required={shouldValidateDescription}
          label={t(
            shouldValidateDescription
              ? "input.labels.requiredDescription"
              : "input.labels.optionalDescription"
          )}
          helperText={t(helperText)}
          value={description}
          onChange={(e) => {
            dispatch(updatePaymentDescription(e.target.value));
          }}
          inputProps={{ style: { width: "250px", height: "63px" }, "data-testid": "payment-description-input" }}
          variant="outlined"
          multiline
        />
      </div>
    </div>
  );
};

export default PaymentDialogContent;
